import { useCallback } from "react";
import { logger, sanitizeError } from "../../utils/logger.js";
import { handleFileDownload } from "../../utils/common.js";
import {
  handleProposalHighlight,
  updateProposal,
} from "../../logic/proposal/index.js";

/** Details page passes nothing for reject, so the button click does not open a dialog. */
const ignoreReject = () => {};

/**
 * Shared approve, star, and download handlers for the proposal details page and modal.
 * Reject stays with the caller: the page has no dialog, the modal opens one.
 * Star and unstar share one highlight call and keep their own error log text.
 * @param {Object} proposal
 * @param {string} [brochureUrl]
 * @param {boolean} isStarred
 * @param {Function} setIsStarred
 * @param {Function} navigate
 * @param {Function} onComplete Runs after a successful approve
 * @param {Function} [onReject]
 */
export const useProposalActions = ({
  proposal,
  brochureUrl,
  isStarred,
  setIsStarred,
  navigate,
  onComplete,
  onReject = ignoreReject,
}) => {
  const setStarred = useCallback(
    async (proposalId, starred, errorMessage) => {
      try {
        setIsStarred(starred);
        await handleProposalHighlight(proposalId, navigate, starred);
      } catch (error) {
        logger.error(errorMessage, { err: sanitizeError(error) });
      }
    },
    [navigate, setIsStarred],
  );

  const onProposalAction = useCallback(
    async (proposalId, action) => {
      if (action === "reject") {
        onReject();
      } else if (action === "approve") {
        try {
          await updateProposal(proposalId, true, navigate, null, isStarred);
          onComplete();
        } catch (error) {
          logger.error("Error approving proposal:", {
            err: sanitizeError(error),
          });
        }
      } else if (action === "star") {
        await setStarred(proposalId, true, "Error highlighting proposal:");
      } else if (action === "unstar") {
        await setStarred(proposalId, false, "Error unhighlighting proposal:");
      } else {
        // Unsupported proposal action. No action required.
      }
    },
    [isStarred, navigate, onComplete, onReject, setStarred],
  );

  const onDownload = useCallback(async (fileUrl) => {
    await handleFileDownload(fileUrl);
  }, []);

  const handleQuoteDownload = useCallback(() => {
    onDownload(proposal.cotizacion);
  }, [onDownload, proposal.cotizacion]);

  const handleBrochureDownload = useCallback(() => {
    onDownload(brochureUrl);
  }, [brochureUrl, onDownload]);

  const handleUnstar = useCallback(() => {
    onProposalAction(proposal._id, "unstar");
  }, [onProposalAction, proposal._id]);

  const handleStar = useCallback(() => {
    onProposalAction(proposal._id, "star");
  }, [onProposalAction, proposal._id]);

  const handleReject = useCallback(() => {
    onProposalAction(proposal._id, "reject");
  }, [onProposalAction, proposal._id]);

  const handleApprove = useCallback(() => {
    onProposalAction(proposal._id, "approve");
  }, [onProposalAction, proposal._id]);

  return {
    handleApprove,
    handleBrochureDownload,
    handleQuoteDownload,
    handleReject,
    handleStar,
    handleUnstar,
  };
};
